/**
 * Units of measure as actually used in a Nicaraguan pulpería.
 *
 * Weight is sold in pounds, not kilos. Bulk staples move in arrobas and quintales:
 * beans, rice, sugar, maize. A quintal is 100 lb; an arroba is 25 lb. Rope and hose are sold by
 * the vara as often as by the metre, which is why it is here and not rounded away.
 *
 * These are presets that seed a product's sale units — a product can define any unit
 * with any factor, so this list constrains nothing.
 */

import { fromUnits, type Milli } from './quantity.js';

export type UnitKind = 'peso' | 'volumen' | 'conteo' | 'longitud';

export interface UnitPreset {
  code: string;
  label: string;
  plural: string;
  kind: UnitKind;
  /** Base units contained in one of these, in milli. Relative to the product's base unit. */
  factor: Milli;
  /** Which base unit this preset assumes. A quintal only means 100 when the base is a pound. */
  base: string;
}

export const UNIT_PRESETS: readonly UnitPreset[] = [
  { code: 'unidad',  label: 'unidad',      plural: 'unidades',  kind: 'conteo',  factor: fromUnits(1),   base: 'unidad' },
  { code: 'docena',  label: 'docena',      plural: 'docenas',   kind: 'conteo',  factor: fromUnits(12),  base: 'unidad' },
  { code: 'par',     label: 'par',         plural: 'pares',     kind: 'conteo',  factor: fromUnits(2),   base: 'unidad' },

  { code: 'libra',   label: 'libra',       plural: 'libras',    kind: 'peso',    factor: fromUnits(1),   base: 'libra' },
  { code: 'onza',    label: 'onza',        plural: 'onzas',     kind: 'peso',    factor: fromUnits(1/16), base: 'libra' },
  { code: 'arroba',  label: 'arroba',      plural: 'arrobas',   kind: 'peso',    factor: fromUnits(25),  base: 'libra' },
  { code: 'quintal', label: 'quintal',     plural: 'quintales', kind: 'peso',    factor: fromUnits(100), base: 'libra' },
  { code: 'kilo',    label: 'kilo',        plural: 'kilos',     kind: 'peso',    factor: fromUnits(2.20462), base: 'libra' },

  { code: 'litro',   label: 'litro',       plural: 'litros',    kind: 'volumen', factor: fromUnits(1),   base: 'litro' },
  { code: 'galon',   label: 'galón',       plural: 'galones',   kind: 'volumen', factor: fromUnits(3.78541), base: 'litro' },
  { code: 'ml',      label: 'mililitro',   plural: 'mililitros',kind: 'volumen', factor: fromUnits(0.001), base: 'litro' },

  // Mecate, manguera y tela se venden por medida. La vara sigue viva en el comercio pequeño.
  { code: 'metro',   label: 'metro',       plural: 'metros',    kind: 'longitud', factor: fromUnits(1),   base: 'metro' },
  { code: 'vara',    label: 'vara',        plural: 'varas',     kind: 'longitud', factor: fromUnits(0.8359), base: 'metro' },
  { code: 'yarda',   label: 'yarda',       plural: 'yardas',    kind: 'longitud', factor: fromUnits(0.9144), base: 'metro' },
];

/** Containers a pulpería buys in. Factor is set per product — a caja holds whatever it holds. */
export const CONTAINER_PRESETS: readonly string[] = [
  'caja', 'fardo', 'saco', 'bolsa', 'paquete', 'bulto', 'display', 'blister', 'rollo', 'tira',
];

export function findUnitPreset(code: string): UnitPreset | undefined {
  return UNIT_PRESETS.find((u) => u.code === code);
}
